import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { createEvent } from '../../../../services/eventService';
import { getCommunity } from '../../../../services/communityService';
import { useAuth } from '../../../../contexts/AuthContext';
import { useNavigation } from '../../../../contexts/NavigationContext';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import '../../../MobileModals.css'; // Import the mobile styles
import { getEventTypeDisplayName, DEFAULT_EVENT_TYPES } from '../../../../utils/EventTypesUtil';

// Helper function to format date without timezone issues
const formatLocalDate = (date) => {
    if (!date) return "";
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

function EventFormModal({ event, selectedDate, onClose, onSave }) {

    const { userCommunities } = useAuth();
    const { isMobile } = useNavigation();
    const { t } = useLanguage();
    const [modalRoot, setModalRoot] = useState(null);

    // Event State
    const [eventData, setEventData] = useState({
        title: event?.title || "",
        location: event?.location || "",
        event_type: event?.event_type || "rehearsal",
        time: event?.time ? event.time.slice(0, 5) : "09:00",
        description: event?.description || "",
        visibility: event?.visibility || "community",
        date: selectedDate ? formatLocalDate(selectedDate) : event?.date || "",
        community_id: event?.community_id || null
    });

    // UI State
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [supportedEventTypes, setSupportedEventTypes] = useState([]);
    const [loadingEventTypes, setLoadingEventTypes] = useState(false);

    // Find or create the modal root element
    useEffect(() => {
        let root = document.getElementById('modal-root');
        if (!root) {
            root = document.createElement('div');
            root.id = 'modal-root';
            document.body.appendChild(root);
        }
        setModalRoot(root);

        // Clean up function
        return () => {
            if (root && root.childNodes.length === 0) {
                document.body.removeChild(root);
            }
        };
    }, []);

    // Effect to load supported event types when community changes
    useEffect(() => {
        const fetchSupportedEventTypes = async () => {
            if (!eventData.community_id) {
                // Reset to default event types if no community selected
                setSupportedEventTypes(DEFAULT_EVENT_TYPES);
                return;
            }

            try {
                setLoadingEventTypes(true);
                const community = await getCommunity(eventData.community_id);
                
                if (community && community.supported_event_types) {
                    try {
                        // Parse the event types from the community
                        const types = JSON.parse(community.supported_event_types);
                        setSupportedEventTypes(types);
                        
                        // If current event_type is not in supported list, change to first supported type
                        if (types.length > 0 && !types.includes(eventData.event_type)) {
                            setEventData(prev => ({
                                ...prev,
                                event_type: types[0]
                            }));
                        }
                    } catch (error) {
                        setSupportedEventTypes(DEFAULT_EVENT_TYPES);
                    }
                } else {
                    setSupportedEventTypes(DEFAULT_EVENT_TYPES);
                }
            } catch (error) {
                setSupportedEventTypes(DEFAULT_EVENT_TYPES);
            } finally {
                setLoadingEventTypes(false);
            }
        };
        
        fetchSupportedEventTypes();
    }, [eventData.community_id]);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEventData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // Handle community selection
    const handleCommunitySelect = (e) => {
        const value = e.target.value;
        const communityId = value === "" ? null : Number(value);
        
        setEventData(prevData => {
            const newData = {
                ...prevData,
                community_id: communityId
            };
            return newData;
        });
    };

    // Handle event submission
    const handleEventSubmit = async () => {
        setError(""); // Clear any previous errors
        
        // Validate required fields
        if (!eventData.title || !eventData.date || !eventData.location) {
            setError(t('events.messages.fillRequiredShort'));
            return;
        }

        // Community validation for community events
        if (eventData.visibility === 'community' && !eventData.community_id) {
            setError(t('events.messages.selectCommunityRequired'));
            return;
        }

        try {
            setLoading(true);
            // Creating a new event
            const eventResponse = await createEvent(eventData);

            setLoading(false);
            onSave();
        } catch (error) {
            setLoading(false);
            setError(t('events.messages.failedToSaveEvent') + ': ' + (error.message || t('events.messages.unknownError')));
        }
    };

    // Styling
    const labelStyle = {
        fontSize: isMobile ? '0.75rem' : '0.85rem',
        fontWeight: '500',
        marginBottom: '0.25rem'
    };
    
    const helpTextStyle = {
        fontSize: isMobile ? '0.7rem' : '0.75rem'
    };
    
    const inputFontSize = isMobile ? '0.75rem' : '0.85rem';

    // If modal root is not available yet, don't render anything
    if (!modalRoot) return null;
    
    const modalContent = (
        <div className="modal-overlay">
            <div
                className={`modal-container ${isMobile ? 'mobile' : ''}`}
                style={!isMobile ? { maxWidth: '66vw', margin: '0 auto' } : {}}
            >
                <div className="card border-0 h-100 d-flex flex-column">
                    {/* Title bar removed for mobile version */}
                    {!isMobile && (
                        <div className="card-header py-3 bg-white d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-0">
                                {t('events.actions.newEvent')}
                            </h5>
                            <button type="button" className="btn-close btn-close-sm" onClick={onClose}></button>
                        </div>
                    )}
    
                    <div className={`card-body overflow-auto ${isMobile ? 'p-2 pt-3' : 'p-3'}`}>
                        {/* Close button for mobile (moved from header) */}
                        {isMobile && (
                            <div className="d-flex justify-content-end mb-2">
                                <button type="button" className="btn-close btn-close-sm" onClick={onClose}></button>
                            </div>
                        )}
                        
                        {error && <div className="alert alert-danger py-2">{error}</div>}
    
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">{t('common.loading')}</span>
                                </div>
                            </div>
                        ) : (
                            <div className="event-details-form">
                                <div className="row g-1">
                                    {/* Event Title */}
                                    <div className="col-12 mb-1">
                                        <label htmlFor="title" className="form-label" style={labelStyle}>
                                            {t('events.form.eventTitle')}*
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="title"
                                            name="title"
                                            value={eventData.title}
                                            onChange={handleInputChange}
                                            style={{ fontSize: inputFontSize }}
                                        />
                                    </div>

                                    {/* Date and Time in the same row - always side by side */}
                                    <div className="col-6 mb-1">
                                        <label htmlFor="date" className="form-label" style={labelStyle}>
                                            {t('events.form.date')}*
                                        </label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            id="date"
                                            name="date"
                                            value={eventData.date}
                                            onChange={handleInputChange}
                                            style={{ fontSize: inputFontSize }}
                                        />
                                    </div>

                                    <div className="col-6 mb-1">
                                        <label htmlFor="time" className="form-label" style={labelStyle}>
                                            {t('events.form.time')}
                                        </label>
                                        <input
                                            type="time"
                                            className="form-control"
                                            id="time"
                                            name="time"
                                            value={eventData.time}
                                            onChange={handleInputChange}
                                            style={{ fontSize: inputFontSize }}
                                        />
                                    </div>

                                    {/* Location */}
                                    <div className="col-12 mb-1">
                                        <label htmlFor="location" className="form-label" style={labelStyle}>
                                            {t('events.form.location')}*
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="location"
                                            name="location"
                                            value={eventData.location}
                                            onChange={handleInputChange}
                                            style={{ fontSize: inputFontSize }}
                                        />
                                    </div>

                                    {/* Event Type and Visibility side by side */}
                                    <div className="col-6 mb-1">
                                        <label htmlFor="event_type" className="form-label" style={labelStyle}>
                                            {t('events.form.eventType')}
                                        </label>
                                        <select
                                            className="form-select"
                                            id="event_type"
                                            name="event_type"
                                            value={eventData.event_type}
                                            onChange={handleInputChange}
                                            style={{ fontSize: inputFontSize }}
                                            disabled={loadingEventTypes}
                                        >
                                            {loadingEventTypes ? (
                                                <option value="">{t('events.form.loading')}</option>
                                            ) : (
                                                supportedEventTypes.map(type => (
                                                    <option key={type} value={type}>
                                                        {getEventTypeDisplayName(type, t)}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </div>

                                    <div className="col-6 mb-1">
                                        <label htmlFor="visibility" className="form-label" style={labelStyle}>
                                            {t('events.form.visibility')}
                                        </label>
                                        <select
                                            className="form-select"
                                            id="visibility"
                                            name="visibility"
                                            value={eventData.visibility}
                                            onChange={handleInputChange}
                                            style={{ fontSize: inputFontSize }}
                                        >
                                            <option value="private">{t('events.form.private')}</option>
                                            <option value="community">{t('events.form.community')}</option>
                                        </select>
                                    </div>
                                    
                                    {/* Help text below both dropdowns */}
                                    <div className="col-12 mb-1">
                                        <div className="form-text mt-0" style={helpTextStyle}>
                                            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                            {eventData.visibility === 'community' ?
                                                t('events.visibility.visibleToAll') :
                                                t('events.visibility.visibleToYou')}
                                        </div>
                                    </div>

                                    {/* Community Select - Only show when visibility is set to community */}
                                    {eventData.visibility === 'community' && (
                                        <div className="col-12 mb-1">
                                            <label htmlFor="community_id" className="form-label" style={labelStyle}>
                                                {t('events.form.community')}*
                                            </label>
                                            <select
                                                className="form-select"
                                                id="community_id"
                                                name="community_id"
                                                value={eventData.community_id || ""}
                                                onChange={handleCommunitySelect}
                                                style={{ fontSize: inputFontSize }}
                                            >
                                                <option value="">{t('events.form.selectCommunity')}</option>
                                                {userCommunities && userCommunities.map(community => (
                                                    <option key={community.id} value={community.id}>
                                                        {community.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {!eventData.community_id && (
                                                <div className="form-text mt-0 text-warning" style={helpTextStyle}>
                                                    <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                                    {t('events.messages.selectCommunity')}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Description - single line */}
                                    <div className="col-12 mb-1">
                                        <label htmlFor="description" className="form-label" style={labelStyle}>
                                            {t('events.form.description')}
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="description"
                                            name="description"
                                            value={eventData.description}
                                            onChange={handleInputChange}
                                            style={{ fontSize: inputFontSize }}
                                        />
                                    </div>
                                </div>

                                <div className="text-muted mt-0" style={{...helpTextStyle, fontSize: '0.65rem'}}>
                                    * {t('events.form.requiredFields')}
                                </div>
                            </div>
                        )}
                    </div>
    
                    <div className={`card-footer ${isMobile ? 'py-2' : 'bg-white'} d-flex justify-content-between`}>
                        <div></div>
                        <div>
                            <button
                                type="button"
                                className={`btn ${isMobile ? 'btn-sm' : ''} btn-secondary me-2`}
                                onClick={onClose}
                            >
                                {t('events.actions.cancel')}
                            </button>
                            <button
                                type="button"
                                className={`btn ${isMobile ? 'btn-sm' : ''} btn-primary`}
                                onClick={handleEventSubmit}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                        {t('events.actions.creating')}
                                    </>
                                ) : t('events.actions.create')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, modalRoot);
}

export default EventFormModal;